// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useAdminAuth } from '@/auth/AuthProvider';
import AuthShell, {
  AuthHeaderSwap,
  authInput,
  authLabel,
  authPrimaryButton,
  authErrorBanner,
  authLink,
} from '@/components/Auth/AuthShell';
import GoogleSignInButton from '@/components/Auth/GoogleSignInButton';

const SignInPage = () => {
  const navigate = useNavigate();
  const { login, isLoaded, isSignedIn, activeOrg } = useAdminAuth();

  // Already signed in → go to the dashboard (or setup if no org).
  React.useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate(activeOrg ? '/admin/dashboard' : '/setup', { replace: true });
    }
  }, [isLoaded, isSignedIn, activeOrg, navigate]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const canSubmit = useMemo(
    () => form.email.trim().length > 0 && form.password.trim().length > 0,
    [form],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage('');
    setNeedsPasswordSetup(false);
    setIsSubmitting(true);
    try {
      const result = await login(form.email.trim(), form.password);
      const hasOrg = result && result.admin && (result.admin.organizations || []).length > 0;
      navigate(hasOrg ? '/admin/dashboard' : '/setup', { replace: true });
    } catch (error: any) {
      const data = error && error.response && error.response.data;
      const msg = (data && data.error) || (error && error.message) || 'Unable to sign in. Please try again.';
      // Account exists but has no password (e.g. legacy invite) — offer a set-password path.
      if (data && data.result && data.result.code === 'password_not_set') {
        setNeedsPasswordSetup(true);
      }
      setErrorMessage(String(msg));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell
      headerAction={<AuthHeaderSwap prompt="New to Staty?" to="/sign-up" label="Sign up" />}
      title={<>Welcome <span className="text-ploy-accent-primary-500">back</span></>}
      subtitle="Sign in to manage your leagues"
      footer={
        <>
          Don't have an account?{' '}
          <Link to="/sign-up" className={authLink}>Sign up</Link>
        </>
      }
    >
      {errorMessage && (
        <div className={`${authErrorBanner} flex items-start gap-3`}>
          <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {errorMessage}
        </div>
      )}

      {needsPasswordSetup && (
        <div className="mb-5">
          <Link
            to={`/forgot-password${form.email.trim() ? `?email=${encodeURIComponent(form.email.trim())}` : ''}`}
            className={authPrimaryButton}
          >
            Set your password
          </Link>
        </div>
      )}

      <GoogleSignInButton onError={setErrorMessage} />
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label className={authLabel}>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              className={authInput}
              placeholder="jordan@example.com"
              required
            />
          </div>
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="block font-body-landing text-sm font-semibold text-ploy-neutral-inverse-950">Password</label>
              <Link to="/forgot-password" tabIndex={-1} className="text-xs font-medium text-ploy-neutral-inverse-600 hover:text-ploy-accent-primary-600">
                Forgot password?
              </Link>
            </div>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
              className={authInput}
              placeholder="Enter your password"
              required
            />
          </div>
        </div>
        <button type="submit" className={`${authPrimaryButton} mt-6`} disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="mt-5 text-center text-xs text-ploy-neutral-inverse-600">
        Need help?{' '}
        <a href="https://staty.io/#contact" className={authLink}>Contact support</a>
      </p>
    </AuthShell>
  );
};

export default SignInPage;
